/**
 * 画像ギャラリー（共通コンポーネント）。
 * storage/system/imgs/ 配下の既存画像を一覧表示 + 新規アップロード可能。
 * フォームのロゴ・画像セクション等で再利用。
 *
 * 仕様書: docs/01_spec/spec_line_bot.md A-12.4（Phase 24b）
 */
import { Download, Trash2, Upload } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import {
  deleteObject,
  getDownloadURL,
  listAll,
  ref as storageRef,
  uploadBytes,
} from 'firebase/storage';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Spinner } from '@/components/ui/spinner';
import { toast } from '@/components/ui/toast';
import { storage } from '@/lib/firebase';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** 選択された画像 URL を返す */
  onSelect: (url: string) => void;
  /** モーダルタイトル */
  title?: string;
  /** ストレージパス（デフォルト system/imgs） */
  storagePath?: string;
  /** 最大バイト数（デフォルト 5MB）*/
  maxBytes?: number;
  /** アップロード前の変換フック（canvas リサイズ等）。返した Blob と推奨拡張子をアップロードする。*/
  beforeUpload?: (file: File) => Promise<{ blob: Blob; ext: string } | null>;
  /** サイズ制約の説明文（UI 表示用） */
  hintText?: string;
}

interface ImageItem {
  fullPath: string;
  url: string;
  name: string;
}

const STORAGE_PATH_DEFAULT = 'system/imgs';
const DEFAULT_MAX_BYTES = 5 * 1024 * 1024;

async function listImages(path: string): Promise<ImageItem[]> {
  const ref = storageRef(storage, path);
  const result = await listAll(ref);
  const items = await Promise.all(
    result.items.map(async (it) => {
      const url = await getDownloadURL(it);
      return { fullPath: it.fullPath, url, name: it.name };
    }),
  );
  // 新しい順（ファイル名にタイムスタンプ含むため逆順ソート）
  return items.sort((a, b) => b.name.localeCompare(a.name));
}

export function ImageGalleryDialog({
  open,
  onOpenChange,
  onSelect,
  title = '画像を選択',
  storagePath = STORAGE_PATH_DEFAULT,
  maxBytes = DEFAULT_MAX_BYTES,
  beforeUpload,
  hintText,
}: Props) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [images, setImages] = useState<ImageItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);

  const refresh = async () => {
    setLoading(true);
    try {
      const list = await listImages(storagePath);
      setImages(list);
    } catch (err) {
      toast.error(`画像一覧の取得失敗: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      void refresh();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, storagePath]);

  const handleUpload = async (file: File) => {
    if (!file.type.startsWith('image/')) {
      toast.error('画像ファイルを選択してください');
      return;
    }
    setUploading(true);
    try {
      let blob: Blob = file;
      let ext = file.name.split('.').pop()?.toLowerCase() || 'jpg';
      let contentType = file.type;

      // beforeUpload フックで canvas リサイズ等の前処理
      if (beforeUpload) {
        const transformed = await beforeUpload(file);
        if (!transformed) {
          // フック側でユーザーへエラートースト出した想定
          setUploading(false);
          if (fileInputRef.current) fileInputRef.current.value = '';
          return;
        }
        blob = transformed.blob;
        ext = transformed.ext;
        contentType = transformed.blob.type || `image/${ext}`;
      }

      if (blob.size > maxBytes) {
        toast.error(
          `${(maxBytes / 1024 / 1024).toFixed(0)}MB 以下の画像を選択してください（現 ${(blob.size / 1024 / 1024).toFixed(1)} MB）`,
        );
        setUploading(false);
        if (fileInputRef.current) fileInputRef.current.value = '';
        return;
      }

      const fileName = `${Date.now()}_${Math.random().toString(36).slice(2, 8)}.${ext}`;
      const targetRef = storageRef(storage, `${storagePath}/${fileName}`);
      await uploadBytes(targetRef, blob, { contentType });
      toast.success('アップロードしました');
      await refresh();
    } catch (err) {
      toast.error(`アップロード失敗: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDownload = async (item: ImageItem, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      // Storage の URL はクロスオリジンのため <a download> 直リンクでは保存されない。
      // blob として取得して objectURL 経由でダウンロードする。
      const res = await fetch(item.url);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objectUrl;
      a.download = item.name;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(objectUrl);
    } catch (err) {
      toast.error(`ダウンロード失敗: ${err instanceof Error ? err.message : String(err)}`);
    }
  };

  const handleDelete = async (item: ImageItem, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!window.confirm('この画像を削除します。共有 URL を使っている他のフォームに影響する可能性があります。')) return;
    try {
      const targetRef = storageRef(storage, item.fullPath);
      await deleteObject(targetRef);
      toast.success('削除しました');
      await refresh();
    } catch (err) {
      toast.error(`削除失敗: ${err instanceof Error ? err.message : String(err)}`);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>
            {hintText ??
              `既存の画像から選ぶか、新しい画像をアップロードしてください（${(maxBytes / 1024 / 1024).toFixed(0)}MB 以下）。`}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div className="text-xs text-[var(--text-muted)]">
              {loading ? '読み込み中…' : `${images.length} 件`}
            </div>
            <Button
              type="button"
              variant="primary"
              size="sm"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
            >
              {uploading ? <Spinner className="size-3 mr-1" /> : <Upload className="size-3.5 mr-1" />}
              新規アップロード
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) void handleUpload(f);
              }}
            />
          </div>

          {loading ? (
            <div className="flex items-center justify-center p-12">
              <Spinner />
            </div>
          ) : images.length === 0 ? (
            <div
              className="border-2 border-dashed border-[var(--border)] rounded-lg p-12 text-center cursor-pointer hover:border-[var(--accent)] transition-colors"
              onClick={() => fileInputRef.current?.click()}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => {
                e.preventDefault();
                const f = e.dataTransfer.files?.[0];
                if (f) void handleUpload(f);
              }}
            >
              <Upload className="size-10 mx-auto text-[var(--text-muted)] mb-2" />
              <div className="text-sm text-[var(--text-secondary)]">
                画像がまだありません
              </div>
              <div className="text-xs text-[var(--text-muted)] mt-1">
                クリックまたはドラッグ&ドロップでアップロード
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-3 md:grid-cols-4 gap-2 max-h-[480px] overflow-y-auto">
              {images.map((img) => (
                <button
                  key={img.fullPath}
                  type="button"
                  onClick={() => {
                    onSelect(img.url);
                    onOpenChange(false);
                  }}
                  className="relative group aspect-square rounded border border-[var(--border)] hover:border-[var(--accent)] overflow-hidden bg-[var(--bg-elevated)]"
                  title={img.name}
                >
                  <img
                    src={img.url}
                    alt={img.name}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                  <span
                    onClick={(e) => void handleDownload(img, e)}
                    className="absolute top-1 right-8 size-6 rounded-full bg-black/60 hover:bg-black/80 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                    title="ダウンロード"
                  >
                    <Download className="size-3" />
                  </span>
                  <span
                    onClick={(e) => void handleDelete(img, e)}
                    className="absolute top-1 right-1 size-6 rounded-full bg-[var(--accent-red)] text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                    title="削除"
                  >
                    <Trash2 className="size-3" />
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
            キャンセル
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
